import { BaseService } from '../base';
import type { FinancePaymentMethod } from '../product-payment-config';

export type PolicyChargeStatus =
  /** Link ainda não gerado: nasce alguns dias antes do vencimento (`scheduledFor`). */
  | 'SCHEDULED'
  | 'CREATING'
  | 'CREATION_FAILED'
  | 'PENDING'
  /** PIX Automático: ao menos uma mensalidade paga e outras em aberto. */
  | 'PARTIALLY_PAID'
  | 'PAID'
  | 'REFUSED'
  | 'REFUNDED'
  | 'CHARGEDBACK'
  | 'EXPIRED'
  | 'ERROR'
  /** Encerrada sem link: quitada ou cancelada no I4Pro antes do dia de gerar. */
  | 'CANCELLED';

export type PolicyChargeAttemptOutcome =
  | 'IN_FLIGHT'
  | 'CREATED'
  | 'ADOPTED'
  | 'PROVIDER_ERROR'
  | 'INVALID'
  | 'THROTTLED'
  | 'UNKNOWN'
  | 'SUPERSEDED';

export type PolicyChargeCreatedVia =
  | 'WORKFLOW'
  | 'SCHEDULER'
  | 'RECONCILIATION'
  | 'MANUAL';

/**
 * `INSTALLMENT`: um link por parcela. `POLICY`: cobrança única de todas as
 * parcelas, parcelada no cartão. `RECURRENCE`: mandato do PIX Automático.
 * Os dois últimos dizem em `coveredInstallments` quais parcelas cobrem.
 */
export type PolicyChargeScope = 'INSTALLMENT' | 'POLICY' | 'RECURRENCE';

export type PolicyChargeCancelReason =
  | 'I4PRO_SETTLED'
  | 'I4PRO_CANCELLED'
  /** PIX Automático cancelado (por nós ou pelo segurado no banco). */
  | 'MANDATE_CANCELED'
  /** PIX Automático não criado a tempo da 1ª mensalidade (o banco exige hoje + 2). */
  | 'MANDATE_START_MISSED';

/** Cancelamento do mandato pedido por nós (apólice cancelada ou financeiro). */
export type PolicyMandateCancelRequest = {
  reason: 'POLICY_CANCELLED' | 'MANUAL';
  requestedAt: string;
  userName?: string;
  note?: string;
};

export type PolicyChargeNotificationStatus = 'SENT' | 'FAILED' | 'SKIPPED';

/** Envio de um link ao segurado por um canal (hoje, só e-mail). */
export type PolicyChargeNotification = {
  channel: 'EMAIL' | 'SMS' | 'WHATSAPP';
  /** Tentativa (link) a que o envio se refere. */
  attempt: number;
  status: PolicyChargeNotificationStatus;
  tries: number;
  sentAt?: string;
  /** Destino mascarado (`fu***@dominio.com`). */
  recipient?: string;
  error?: string;
  updatedAt?: string;
};

/** Estado atual da cobrança de uma parcela. */
export type PolicyCharge = {
  /** `0` na cobrança única (`scope: 'POLICY'`) — ver `coveredInstallments`. */
  installmentNumber: number;
  installmentCount?: number;
  scope?: PolicyChargeScope;
  coveredInstallments?: number[];
  cardInstallments?: number;
  /** Quando o link nasce (ou nasceu). */
  scheduledFor?: string;
  cancelReason?: PolicyChargeCancelReason;
  /** Último envio do link ao segurado, por canal. */
  notifications?: PolicyChargeNotification[];
  /** PIX Automático: valor de cada mensalidade. */
  cycleAmount?: number;
  /** PIX Automático: mensalidades pagas. */
  paidInstallments?: number;
  settledInstallments?: number;
  /** PIX Automático: 1ª mensalidade (`YYYY-MM-DD`). */
  recurrenceFirstDueDate?: string;
  /** PIX Automático: quando o segurado autorizou. Ausente com PENDING = aguardando. */
  recurrenceAuthorizedAt?: string;
  /** PIX Automático: quem cancelou — `RECEIVER` (o recebedor) ou `PAYER` (o segurado). */
  mandateCanceledBy?: string;
  /** PIX Automático: até qual mensalidade o atraso já foi avisado. */
  lateAlertedInstallment?: number;
  /** Presente com o mandato ainda ativo = cancelamento pedido e pendente. */
  mandateCancelRequest?: PolicyMandateCancelRequest;
  amount: number;
  dueDate: string;
  status: PolicyChargeStatus;
  paymentUrl?: string;
  paymentMethods?: FinancePaymentMethod[];
  orderId?: string;
  expireDate?: string;
  attemptCount: number;
  activeAttempt?: number;
  paidAt?: string;
  /** Pode não ser a tentativa ativa, quando o segurado paga um link antigo. */
  paidByAttempt?: number;
  errorMessage?: string;
  updatedAt?: string;
};

/** Estorno no cartão: REQUESTED/PROCESSING/PENDING = em andamento. */
export type PolicyChargeRefundStatus =
  | 'REQUESTED'
  | 'PROCESSING'
  | 'PENDING'
  | 'SUCCEEDED'
  | 'FAILED';

export type PolicyChargeRefund = {
  /** Parcelas devolvidas por este estorno. */
  installments: number[];
  amount: number;
  status: PolicyChargeRefundStatus;
  failureCode?: string;
  failureMessage?: string;
  reason: string;
  requestedByUserName?: string;
  requestedAt: string;
  completedAt?: string;
};

export type RefundPolicyChargesResult = {
  /** Os estornos criados agora. */
  created: PolicyChargeRefund[];
  refunds: PolicyChargeRefund[];
  charges: PolicyCharge[];
};

/** Resultado do "Verificar no banco" do PIX Automático. */
export type PolicyRecurrenceRefreshResult = {
  /** O banco mostrou algo que o Portal não sabia (e já foi aplicado). */
  changed: boolean;
  /** PENDING, ACCEPTED, ACTIVE, CANCELLED_RECEIVER, CANCELLED_PAYER, EXPIRED ou REJECTED. */
  providerStatus?: string;
  status?: PolicyChargeStatus;
  charges: PolicyCharge[];
};

/** Uma tentativa de criar a cobrança — inclusive as que falharam. */
export type PolicyChargeAttempt = {
  installmentNumber: number;
  attempt: number;
  requestedAt: string;
  finishedAt?: string;
  amount: number;
  dueDate: string;
  outcome: PolicyChargeAttemptOutcome;
  /** `RECURRENCE` = mandato do PIX Automático; os demais são link de pagamento. */
  billingFlow?: string;
  httpStatus?: number;
  errorMessage?: string;
  orderId?: string;
  paymentUrl?: string;
  orderStatus?: string;
  createdVia: PolicyChargeCreatedVia;
  createdByUserName?: string;
  manualAmountReason?: string;
  /** Envio DESTE link ao segurado. */
  notifications?: PolicyChargeNotification[];
};

export type CreateManualChargePayload = {
  installmentNumber: number;
  /** Valor final, com juros e multa já calculados pelo financeiro. */
  amount: number;
  dueDate: string;
  reason: string;
};

/**
 * O `policyId` do InsureMO contém vírgula (`1007074001,09BCD...`), então todo
 * caminho precisa de `encodeURIComponent` — mesmo cuidado dos endpoints de
 * parcelas.
 */
function chargesPath(policyId: string, suffix = ''): string {
  return `/portal/v1/policies/${encodeURIComponent(policyId)}/charges${suffix}`;
}

export class PolicyChargesService extends BaseService {
  /** Lista vazia quando o produto não usa cobrança por link. */
  async getCharges(policyId: string): Promise<{
    success: boolean;
    data?: PolicyCharge[];
    error?: string;
  }> {
    try {
      const response = await this.get(
        chargesPath(policyId),
        {},
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<PolicyCharge[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar as cobranças da apólice',
      };
    }
  }

  /** Histórico completo de tentativas, incluindo as que falharam. */
  async getAttempts(
    policyId: string,
    installmentNumber?: number
  ): Promise<{
    success: boolean;
    data?: PolicyChargeAttempt[];
    error?: string;
  }> {
    try {
      const query =
        installmentNumber != null
          ? `?installmentNumber=${installmentNumber}`
          : '';
      const response = await this.get(
        chargesPath(policyId, `/attempts${query}`),
        {},
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<PolicyChargeAttempt[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar o histórico de tentativas',
      };
    }
  }

  async regenerate(
    policyId: string,
    installmentNumber?: number
  ): Promise<{ success: boolean; data?: PolicyCharge[]; error?: string }> {
    try {
      const response = await this.post(
        chargesPath(policyId, '/regenerate'),
        { installmentNumber },
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<PolicyCharge[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao gerar o link de pagamento',
      };
    }
  }

  /** Cancela o PIX Automático da apólice (perfil financeiro). */
  async cancelRecurrence(
    policyId: string,
    reason: string
  ): Promise<{ success: boolean; data?: PolicyCharge[]; error?: string }> {
    try {
      const response = await this.post(
        chargesPath(policyId, '/cancel-recurrence'),
        { reason },
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<PolicyCharge[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao cancelar o PIX Automático',
      };
    }
  }

  /** Estornos da apólice (os em andamento são conferidos antes na Finance.API). */
  async getRefunds(policyId: string): Promise<{
    success: boolean;
    data?: PolicyChargeRefund[];
    error?: string;
  }> {
    try {
      const response = await this.get(
        chargesPath(policyId, '/refunds'),
        {},
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<PolicyChargeRefund[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar os estornos',
      };
    }
  }

  /**
   * Estorna no cartão uma parcela paga (o valor inteiro dela) ou, sem parcela,
   * todas as parcelas pagas da apólice. Só administração e financeiro.
   */
  async refund(
    policyId: string,
    payload: { installmentNumber?: number; reason: string }
  ): Promise<{
    success: boolean;
    data?: RefundPolicyChargesResult;
    error?: string;
  }> {
    try {
      const response = await this.post(
        chargesPath(policyId, '/refunds'),
        payload,
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<RefundPolicyChargesResult>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao estornar',
      };
    }
  }

  /**
   * Confere o PIX Automático no BANCO (via Finance.API/Autra) e aplica o que um
   * webhook perdido teria aplicado. Repetir é inofensivo.
   */
  async refreshRecurrence(policyId: string): Promise<{
    success: boolean;
    data?: PolicyRecurrenceRefreshResult;
    error?: string;
  }> {
    try {
      const response = await this.post(
        chargesPath(policyId, '/refresh-recurrence'),
        {},
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<PolicyRecurrenceRefreshResult>(
        response
      );
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao verificar o PIX Automático no banco',
      };
    }
  }

  /** Reenvia ao segurado o link ATIVO da parcela. Não cria link novo. */
  async resendNotification(
    policyId: string,
    installmentNumber: number
  ): Promise<{ success: boolean; data?: PolicyCharge; error?: string }> {
    try {
      const response = await this.post(
        chargesPath(policyId, '/notify'),
        { installmentNumber },
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<PolicyCharge>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao reenviar o link ao segurado',
      };
    }
  }

  async createManual(
    policyId: string,
    payload: CreateManualChargePayload
  ): Promise<{ success: boolean; data?: PolicyCharge; error?: string }> {
    try {
      const response = await this.post(
        chargesPath(policyId, '/manual'),
        payload,
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<PolicyCharge>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao criar a cobrança manual',
      };
    }
  }
}

export const policyChargesService = new PolicyChargesService();
